if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.storefrontAccessToken = '0f58e6c29c636bd4fb0bd0bcbe49eb19';
        this.shopUrl = `https://${window.location.hostname}/api/2024-01/graphql.json`;
        this._selectedOptionMap = {};
        this._lastVariant = null;
        this._priceSyncObserver = null;
        this._priceSyncTimer = null;
        this._isSyncingPrice = false;
        this._pipingFilterTimers = [];
        this._pipingFilterObserver = null;
        this._pipingFilterHandlers = null;
        this._pipingAvailabilityVariants = null;
      }

      get selectedOptionValues() {
        const checked = Array.from(
          this.querySelectorAll('input[type="radio"]:checked, select option[selected]')
        );
        return checked.map(({ dataset }) => dataset.optionValueId).filter(Boolean);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.ensureVariantIsSelected();
        this.loadMissingSwatchesFromApi();
        this._seedOptionMapFromDOM();
        this._initPipingAvailabilityFilter();

        document.addEventListener('change', (e) => {
          if (!e.target.matches('.drawer-swatch-input')) return;

          const value   = e.target.value;
          const optName = e.target.name;

          const cleanOptionName = optName.replace(/-\d+$/, '');
          this._selectedOptionMap[cleanOptionName] = value;

          const sliderInput = this.querySelector(
            `input[type="radio"][name="${optName}"][value="${CSS.escape(value)}"]`
          );
          if (sliderInput && !sliderInput.checked) {
            sliderInput.checked = true;
            sliderInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const labelSpan = this.querySelector(
            `[data-selected-swatch-value="${cleanOptionName}"]`
          );
          if (labelSpan) {
            const cleanName =
              e.target.closest('[data-clean-name]')?.dataset.cleanName ||
              value.split('-').pop().replace(/_/g, ' ').trim();
            labelSpan.textContent = cleanName;
          }

          this._schedulePipingAvailabilityFilter([0, 80, 220, 500, 900]);
        });

        document.addEventListener('variant:changed', (event) => {
          this._schedulePipingAvailabilityFilter([0, 80, 220, 500, 900, 1400]);
          const drawer  = document.querySelector('product-drawer');
          const variant = event.detail.variant;
          if (!variant) return;

          // Keep selected variant price visible after theme AJAX re-render.
          this._lastVariant = variant;
          this._syncVariantPrice(variant);
          setTimeout(() => this._syncVariantPrice(variant), 25);
          setTimeout(() => this._syncVariantPrice(variant), 120);

          const optionValues = [variant.option1, variant.option2, variant.option3];

          document.querySelectorAll('.swiper.swiper-cc-t').forEach((swiperEl, swiperIndex) => {
            const currentOptionValue = optionValues[swiperIndex];
            if (!currentOptionValue) return;

            const swiperInstance = swiperEl.swiper;
            if (!swiperInstance) return;

            swiperEl.querySelectorAll('.swiper-slide').forEach(slide => {
              slide.classList.remove('active-swatch-option');
            });

            let matchingSlide = swiperEl.querySelector(
              `.swiper-slide[data-value="${CSS.escape(currentOptionValue)}"]`
            );

            if (matchingSlide) {
              matchingSlide.classList.add('active-swatch-option');
              const slideIndex = [...swiperEl.querySelectorAll('.swiper-slide')].indexOf(matchingSlide);
              swiperInstance.slideTo(slideIndex, 0);
            }

            if (drawer) {
              drawer.removeAttribute('open');
              drawer.removeAttribute('active');
            }
          });
        });

        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);

          if (target.type === 'radio' || target.tagName === 'SELECT') {
            const optName = (target.name || '').replace(/-\d+$/, '');
            if (optName) {
              this._selectedOptionMap[optName] = target.value;
            }
          }

          this.updateSelectedSwatchValue(event);
          this._schedulePipingAvailabilityFilter([0, 80, 220, 500, 900, 1400]);

          if (window.FoxTheme && window.FoxTheme.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          }
        });

        this._observeForRerender();
        this._bindVariantChangedFix();
        this._bindSmoothPriceSync();
      }

      _seedOptionMapFromDOM() {
        this.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
          const optName = (radio.name || '').replace(/-\d+$/, '');
          if (optName && radio.value) {
            this._selectedOptionMap[optName] = radio.value;
          }
        });
        this.querySelectorAll('select').forEach(select => {
          const optName = (select.name || '').replace(/options\[|\]/g, '');
          if (optName && select.value) {
            this._selectedOptionMap[optName] = select.value;
          }
        });
      }

      _observeForRerender() {
  const parent = this.parentElement;
  if (!parent) return;

  const observer = new MutationObserver((mutations) => {
    let shouldRestore = false;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      const newVariantSelects = parent.querySelector('variant-selects');
      if (newVariantSelects && newVariantSelects !== this) {
        newVariantSelects._selectedOptionMap = { ...this._selectedOptionMap };
        observer.disconnect();
        return;
      }
      shouldRestore = true;
    }

    if (shouldRestore) {
      // FIX: Temporarily pause the observer to prevent the infinite loop
      observer.disconnect(); 
      this._restoreSelections();
      // Reconnect after DOM modifications are finished
      observer.observe(parent, { childList: true, subtree: true }); 
    }
  });

  observer.observe(parent, { childList: true, subtree: true });
  this._rerenderObserver = observer;
}

      _restoreSelections() {
        if (!Object.keys(this._selectedOptionMap).length) return;

        let restored = false;

        Object.entries(this._selectedOptionMap).forEach(([optName, value]) => {
          const radios = this.querySelectorAll(
            `input[type="radio"][value="${CSS.escape(value)}"]`
          );

          radios.forEach(radio => {
            const radioOptName = (radio.name || '').replace(/-\d+$/, '');
            if (radioOptName === optName && !radio.checked) {
              radio.checked = true;
              restored = true;

              let labelSpan = null;
              this.querySelectorAll('[data-selected-swatch-value]').forEach(span => {
                if (span.dataset.selectedSwatchValue === optName) labelSpan = span;
              });
              if (labelSpan) {
  const cleanName =
    radio.closest('[data-clean-name]')?.dataset.cleanName ||
    value.split('-').pop().replace(/_/g, ' ').trim();
    
  // FIX: Only update the DOM if the text is actually changing
  if (labelSpan.textContent !== cleanName) {
    labelSpan.textContent = cleanName;
  }
}
            }
          });
        });

        if (restored) {
          // Do NOT publish optionValueSelectionChange during DOM restore.
          // Publishing here causes the product section to re-render again,
          // which can flash/swap the price back to the initial lowest price.
          this.updateFormButtonState();
          if (this._lastVariant) {
            this._syncVariantPrice(this._lastVariant);
          }
        }
      }

      async loadMissingSwatchesFromApi() {
        const sliders = this.querySelectorAll('.swiper-variant-picker[data-metafield-key]');

        sliders.forEach(async (slider) => {
          const emptySwatches = Array.from(slider.querySelectorAll('.swatch-color-display')).filter(el => {
            const style = window.getComputedStyle(el);
            const bg = style.getPropertyValue('--swatch-background') || el.style.getPropertyValue('--swatch-background');
            return !bg || !bg.includes('url(');
          });

          if (emptySwatches.length === 0) return;

          const handle    = slider.dataset.productHandle;
          const namespace = slider.dataset.metafieldNamespace || 'custom';
          const key       = slider.dataset.metafieldKey;

          const nameKeys  = (slider.dataset.nameKeys  || 'name').split(',').map(k => k.trim().toLowerCase());
          const imageKeys = (slider.dataset.imageKeys || 'image').split(',').map(k => k.trim().toLowerCase());

          if (!handle || !key) return;

          try {
            const metaobjects = await this.fetchMetaobjects(handle, namespace, key);
            if (!metaobjects || metaobjects.length === 0) return;

            this.updateSwatchesWithApiData(slider, metaobjects, nameKeys, imageKeys);

            const drawerID = slider.id.replace('swiper-', 'drawer-');
            const drawer   = document.getElementById(drawerID);
            if (drawer) {
              this.updateSwatchesWithApiData(drawer, metaobjects, nameKeys, imageKeys);
            }
          } catch (err) {
            console.error('VariantSelects: API Fetch failed', err);
          }
        });
      }

      async fetchMetaobjects(handle, namespace, key) {
        const query = `
          query getProductMetafields($handle: String!, $namespace: String!, $key: String!) {
            product(handle: $handle) {
              metafield(namespace: $namespace, key: $key) {
                references(first: 250) {
                  nodes {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image {
                              url(transform: {maxWidth: 200})
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        const response = await fetch(this.shopUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
          },
          body: JSON.stringify({ query, variables: { handle, namespace, key } }),
        });

        if (response.status === 401) {
          console.error('VariantSelects: 401 Unauthorized. Check your Storefront Access Token.');
          return [];
        }

        const json = await response.json();
        return json.data?.product?.metafield?.references?.nodes || [];
      }

      updateSwatchesWithApiData(container, metaobjects, nameKeys, imageKeys) {
        const normalize = (str) => str ? str.toLowerCase().replace(/[-_ ]/g, '') : '';
        const dataMap   = {};

        metaobjects.forEach(obj => {
          let name     = '';
          let imageUrl = '';

          obj.fields.forEach(field => {
            const fKey = field.key.toLowerCase();
            if (!name     && (nameKeys.includes(fKey)  || fKey === 'name'))  name     = field.value;
            if (!imageUrl && (imageKeys.includes(fKey) || fKey === 'image')) {
              if (field.reference?.image) imageUrl = field.reference.image.url;
              else if (field.value && field.value.includes('http')) imageUrl = field.value;
            }
          });

          if (name && imageUrl) {
            dataMap[normalize(name)] = imageUrl;
          }
        });

        const swatches = container.querySelectorAll('.swatch-color-display');

        swatches.forEach(swatch => {
          const style  = window.getComputedStyle(swatch);
          const bgVar  = style.getPropertyValue('--swatch-background') || swatch.style.getPropertyValue('--swatch-background');
          const bgImg  = swatch.style.backgroundImage || swatch.style.background;

          if ((bgVar && bgVar.includes('url(')) || (bgImg && bgImg.includes('url('))) return;

          const parent = swatch.closest('.swiper-slide, .drawer-swatch-item');
          if (!parent) return;

          const rawValue      = parent.dataset.value || '';
          const cleanValueKey = normalize(rawValue);
          const parts         = rawValue.split('-');
          const lastPart      = parts[parts.length - 1];
          const suffixKey     = normalize(lastPart);
          const dataCleanKey  = normalize(parent.dataset.cleanName);

          const foundImage = dataMap[suffixKey] || dataMap[dataCleanKey] || dataMap[cleanValueKey];

          if (foundImage) {
            swatch.style.setProperty('--swatch-background', `url('${foundImage}')`);
            swatch.style.backgroundImage   = `url('${foundImage}')`;
            swatch.style.backgroundSize    = 'cover';
            swatch.style.backgroundPosition = 'center';
            swatch.classList.add('api-loaded-swatch');
          }
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            ?.querySelector('[data-selected-value] > .swatch');

          if (selectedDropdownSwatchValue) {
            if (swatchValue) {
              selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
              selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
            } else {
              selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
              selectedDropdownSwatchValue.classList.add('swatch--unavailable');
            }
          }
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const radioOptName = (target.name || '').replace(/-\d+$/, '');

          let selectedSwatchValue = null;
          try {
            selectedSwatchValue = this.querySelector(
              `[data-selected-swatch-value="${CSS.escape(radioOptName)}"]`
            );
          } catch (_) {}

          if (!selectedSwatchValue) {
            this.querySelectorAll('[data-selected-swatch-value]').forEach(span => {
              if (span.dataset.selectedSwatchValue === radioOptName) {
                selectedSwatchValue = span;
              }
            });
          }

          if (!selectedSwatchValue) {
            selectedSwatchValue = target
              .closest('.product-form__input')
              ?.querySelector('[data-selected-swatch-value]');
          }

          if (selectedSwatchValue) {
            const cleanName =
              target.closest('[data-clean-name]')?.dataset.cleanName ||
              value.split('-').pop().replace(/_/g, ' ').trim();
            selectedSwatchValue.textContent = cleanName;
          }
        }

        this.updateFormButtonState();
      }


      _normalizePipingFilterText(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[_-]+/g, ' ')
          .replace(/[():]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/^choose\s+your\s+/, '')
          .replace(/^choose\s+/, '')
          .replace(/\s+find$/, '')
          .trim();
      }

      _getPipingFilterField() {
        const fields = Array.from(
          this.querySelectorAll('[data-option-name][data-option-position]')
        );

        return (
          fields.find((field) => {
            return (
              field.dataset.pipingOption === 'true' ||
              this._normalizePipingFilterText(field.dataset.optionName) === 'piping'
            );
          }) || null
        );
      }

      _getPipingFilterSelectedValue(field) {
        if (!field) return '';

        const checked =
          field.querySelector('input[type="radio"]:checked:not(.drawer-swatch-input)') ||
          field.querySelector('input[type="radio"]:checked');

        if (checked) return checked.value || '';

        const select = field.querySelector('select');
        if (select) return select.value || '';

        const optionName = field.dataset.optionName || '';
        return this._selectedOptionMap[optionName] || '';
      }

      _isWithPipingSelected() {
        const field = this._getPipingFilterField();
        if (!field) return false;

        const selected = this._normalizePipingFilterText(
          this._getPipingFilterSelectedValue(field)
        );

        return (
          selected === 'with piping' ||
          (selected.includes('with piping') && !selected.includes('without piping'))
        );
      }

      _getPipingFilterDrawers(pickerRoot) {
        if (!pickerRoot) return [];

        const found = new Set();
        const ownerId = pickerRoot.id || '';

        if (ownerId) {
          const escapedOwner = CSS.escape(ownerId);

          document
            .querySelectorAll(
              `[data-piping-filter-drawer][data-piping-filter-owner="${escapedOwner}"]`
            )
            .forEach((drawer) => found.add(drawer));
        }

        pickerRoot
          .querySelectorAll('[data-piping-filter-drawer], .variant-drawer[data-drawer]')
          .forEach((drawer) => found.add(drawer));

        pickerRoot
          .querySelectorAll('[data-drawer-trigger]')
          .forEach((trigger) => {
            const drawerId = trigger.dataset.drawerTrigger;
            if (!drawerId) return;

            document
              .querySelectorAll(`[id="${CSS.escape(drawerId)}"]`)
              .forEach((drawer) => found.add(drawer));
          });

        const optionPosition = pickerRoot.dataset.optionPosition || '';
        const optionName = pickerRoot.dataset.optionName || '';

        if (optionPosition) {
          document
            .querySelectorAll(
              `.variant-drawer[data-drawer][data-option-position="${CSS.escape(optionPosition)}"]`
            )
            .forEach((drawer) => {
              if (
                !optionName ||
                !drawer.dataset.optionName ||
                drawer.dataset.optionName === optionName
              ) {
                found.add(drawer);
              }
            });
        }

        return Array.from(found);
      }

      _getPipingAvailabilityVariants() {
        if (Array.isArray(this._pipingAvailabilityVariants)) {
          return this._pipingAvailabilityVariants;
        }

        const dataElement = this.querySelector(
          '[data-piping-availability-variants]'
        );

        if (!dataElement) {
          this._pipingAvailabilityVariants = [];
          return this._pipingAvailabilityVariants;
        }

        try {
          const parsed = JSON.parse(dataElement.textContent || '[]');
          this._pipingAvailabilityVariants = Array.isArray(parsed)
            ? parsed.filter(
                (variant) =>
                  variant &&
                  Array.isArray(variant.options) &&
                  variant.available === true
              )
            : [];
        } catch (error) {
          console.warn(
            'VariantSelects: Piping availability data could not be parsed.',
            error
          );
          this._pipingAvailabilityVariants = [];
        }

        return this._pipingAvailabilityVariants;
      }

      _getSelectedOptionValuesByPosition() {
        const selected = new Map();
        const fields = Array.from(
          this.querySelectorAll('[data-option-position][data-option-name]')
        );

        fields.forEach((field) => {
          const position = Number(field.dataset.optionPosition || 0);
          if (!position || selected.has(position)) return;

          const checked =
            field.querySelector(
              'input[type="radio"]:checked:not(.drawer-swatch-input)'
            ) || field.querySelector('input[type="radio"]:checked');

          if (checked?.value) {
            selected.set(position, checked.value);
            return;
          }

          const select = field.querySelector('select');
          if (select?.value) selected.set(position, select.value);
        });

        return selected;
      }

      _getPipingFilterItemValue(item) {
        if (!item) return '';

        return (
          item.dataset?.value ||
          item.querySelector?.('input[type="radio"], option')?.value ||
          (item.matches?.('input, option') ? item.value : '') ||
          ''
        );
      }

      _isPipingOptionValueAvailable(
        pickerRoot,
        item,
        availableVariants,
        selectedByPosition
      ) {
        if (!pickerRoot || !item) return false;

        const optionPosition = Number(
          pickerRoot.dataset.optionPosition || 0
        );
        const optionValue = this._getPipingFilterItemValue(item);

        if (!optionPosition || !optionValue) return false;

        if (!availableVariants.length) {
          return this._isMainVariantChoiceAvailable(item);
        }

        return availableVariants.some((variant) => {
          if (variant.options[optionPosition - 1] !== optionValue) {
            return false;
          }

          for (const [position, selectedValue] of selectedByPosition) {
            /*
              Match only the selections before the option being filtered.
              This is the same progressive availability rule Shopify uses and
              avoids hiding valid choices because of a stale later selection.
            */
            if (position >= optionPosition || !selectedValue) continue;

            if (variant.options[position - 1] !== selectedValue) {
              return false;
            }
          }

          return true;
        });
      }

      _isMainVariantChoiceAvailable(item) {
        if (!item) return false;

        const input = item.matches('input')
          ? item
          : item.querySelector('input[type="radio"], option');

        if (!input) return false;

        /*
          The theme updates disabled/class/aria-disabled live.
          These live states take priority over Liquid data attributes,
          which may still contain the previous AJAX state.
        */
        if (
          input.disabled ||
          input.classList.contains('disabled') ||
          input.classList.contains('is-disabled') ||
          input.getAttribute('aria-disabled') === 'true' ||
          item.classList.contains('disabled') ||
          item.classList.contains('is-disabled') ||
          item.getAttribute('aria-disabled') === 'true'
        ) {
          return false;
        }

        const explicitAvailability =
          input.dataset?.optionAvailable || item.dataset?.optionAvailable || '';

        if (explicitAvailability === 'false') return false;
        if (explicitAvailability === 'true') return true;

        return true;
      }

      _findMainChoiceForDrawerItem(pickerRoot, drawerItem) {
        if (!pickerRoot || !drawerItem) return null;

        const optionValueId =
          drawerItem.dataset.optionValueId ||
          drawerItem.querySelector('input')?.dataset.optionValueId ||
          '';

        if (optionValueId) {
          const byId = pickerRoot.querySelector(
            `.variant-swatch-slide[data-option-value-id="${CSS.escape(optionValueId)}"]`
          );
          if (byId) return byId;
        }

        const value =
          drawerItem.dataset.value ||
          drawerItem.querySelector('input')?.value ||
          '';

        if (!value) return null;

        return pickerRoot.querySelector(
          `.variant-swatch-slide[data-value="${CSS.escape(value)}"]`
        );
      }

      _setPipingFilterHidden(element, hidden) {
        if (!element) return;

        element.classList.toggle('piping-filter-hidden', hidden);

        /*
          Swiper/theme CSS can declare .swiper-slide with display using
          !important. In that case a normal helper class is not strong enough,
          even though the same class works correctly inside the drawer.

          Apply an inline !important display only to the main slider slide.
          Drawer items keep using the existing class-based behavior.
        */
        const isMainSliderSlide = element.matches?.(
          '.variant-swatch-slide, .swiper-slide[data-piping-filter-item]'
        );

        if (isMainSliderSlide) {
          if (hidden) {
            element.style.setProperty('display', 'none', 'important');
          } else {
            element.style.removeProperty('display');
          }
        }

        if (hidden) element.setAttribute('aria-hidden', 'true');
        else element.removeAttribute('aria-hidden');
      }

      _updatePipingFilterSwiper(pickerRoot) {
        const runUpdate = () => {
          pickerRoot.querySelectorAll('.swiper').forEach((swiperElement) => {
            const swiper = swiperElement.swiper;
            if (!swiper) return;

            try {
              swiper.updateSlides?.();
              swiper.updateSize?.();
              swiper.updateProgress?.();
              swiper.updateSlidesClasses?.();
              swiper.update?.();
            } catch (error) {
              console.warn('VariantSelects: Swiper availability update failed.', error);
            }
          });
        };

        window.requestAnimationFrame(runUpdate);
        window.setTimeout(runUpdate, 80);
      }

      _applyPipingAvailabilityFilter() {
        if (!this.isConnected) return;

        const pipingField = this._getPipingFilterField();
        const hasExactPipingOption = !!pipingField;
        const filterActive =
          hasExactPipingOption && this._isWithPipingSelected();
        const pipingPosition = Number(
          pipingField?.dataset.optionPosition || 0
        );
        const availableVariants = this._getPipingAvailabilityVariants();
        const selectedByPosition =
          this._getSelectedOptionValuesByPosition();

        this.dataset.pipingAvailabilityActive = filterActive
          ? 'true'
          : 'false';

        const pickerRoots = Array.from(
          this.querySelectorAll(
            '[data-piping-filter-root][data-option-position]'
          )
        );

        pickerRoots.forEach((pickerRoot) => {
          const optionPosition = Number(
            pickerRoot.dataset.optionPosition || 0
          );
          const shouldFilterThisOption =
            filterActive &&
            pipingPosition > 0 &&
            optionPosition > pipingPosition;

          const mainSlides = Array.from(
            pickerRoot.querySelectorAll('.variant-swatch-slide')
          );

          mainSlides.forEach((slide) => {
            const available = shouldFilterThisOption
              ? this._isPipingOptionValueAvailable(
                  pickerRoot,
                  slide,
                  availableVariants,
                  selectedByPosition
                )
              : true;

            this._setPipingFilterHidden(
              slide,
              shouldFilterThisOption && !available
            );
          });

          const drawers = this._getPipingFilterDrawers(pickerRoot);

          drawers.forEach((drawer) => {
            const drawerItems = Array.from(
              drawer.querySelectorAll('.drawer-swatch-item')
            );

            drawerItems.forEach((drawerItem) => {
              const mainChoice = this._findMainChoiceForDrawerItem(
                pickerRoot,
                drawerItem
              );
              const sourceItem = mainChoice || drawerItem;
              const available = shouldFilterThisOption
                ? this._isPipingOptionValueAvailable(
                    pickerRoot,
                    sourceItem,
                    availableVariants,
                    selectedByPosition
                  )
                : true;

              this._setPipingFilterHidden(
                drawerItem,
                shouldFilterThisOption && !available
              );
            });

            drawer
              .querySelectorAll(
                '.drawer-accordion-group[data-accordion-group]'
              )
              .forEach((group) => {
                const items = Array.from(
                  group.querySelectorAll('.drawer-swatch-item')
                );

                const hasVisibleItem = items.some(
                  (item) =>
                    !item.classList.contains('piping-filter-hidden')
                );

                const hideGroup =
                  shouldFilterThisOption &&
                  items.length > 0 &&
                  !hasVisibleItem;

                group.classList.toggle(
                  'piping-filter-empty-group',
                  hideGroup
                );

                if (hideGroup) group.setAttribute('aria-hidden', 'true');
                else group.removeAttribute('aria-hidden');
              });
          });

          this._updatePipingFilterSwiper(pickerRoot);
        });
      }

      _schedulePipingAvailabilityFilter(
        delays = [0, 80, 220, 500, 900]
      ) {
        this._pipingFilterTimers.forEach((timer) =>
          window.clearTimeout(timer)
        );

        this._pipingFilterTimers = delays.map((delay) =>
          window.setTimeout(() => {
            if (this.isConnected) {
              this._applyPipingAvailabilityFilter();
            }
          }, delay)
        );
      }

      _initPipingAvailabilityFilter() {
        if (this._pipingFilterHandlers) {
          this._schedulePipingAvailabilityFilter();
          return;
        }

        const schedule = () => {
          this._schedulePipingAvailabilityFilter(
            [0, 80, 220, 500, 900, 1400]
          );
        };

        const clickHandler = (event) => {
          const target = event.target;
          if (!target?.closest) return;

          if (
            target.closest(
              '[data-piping-option], [data-drawer-trigger], .option-view-all-btn'
            )
          ) {
            schedule();
          }
        };

        const changeHandler = (event) => {
          const target = event.target;
          if (!target?.matches) return;

          if (
            target.matches(
              'input[type="radio"], select, .drawer-swatch-input'
            )
          ) {
            schedule();
          }
        };

        const variantHandler = () => schedule();

        document.addEventListener('click', clickHandler, true);
        document.addEventListener('change', changeHandler, true);
        document.addEventListener('variant:change', variantHandler, true);
        document.addEventListener('variant:changed', variantHandler, true);
        document.addEventListener('shopify:section:load', variantHandler, true);

        this._pipingFilterHandlers = {
          clickHandler,
          changeHandler,
          variantHandler,
        };

        this._pipingFilterObserver = new MutationObserver((mutations) => {
          const relevant = mutations.some((mutation) => {
            if (mutation.type === 'childList') return true;

            if (mutation.type !== 'attributes') return false;

            return [
              'class',
              'disabled',
              'aria-disabled',
              'data-option-available',
              'checked',
            ].includes(mutation.attributeName);
          });

          if (relevant) schedule();
        });

        const observeTarget =
          this.closest('[id^="ProductInfo-"]') ||
          this.parentElement ||
          this;

        this._pipingFilterObserver.observe(observeTarget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            'class',
            'disabled',
            'aria-disabled',
            'data-option-available',
            'checked',
          ],
        });

        /*
          Some themes move the drawer outside <variant-selects>.
          Watch document.body as well so the portal drawer is filtered live.
        */
        if (document.body && observeTarget !== document.body) {
          this._pipingPortalObserver = new MutationObserver((mutations) => {
            const relevant = mutations.some((mutation) => {
              if (mutation.type === 'childList') return true;
              if (mutation.type !== 'attributes') return false;

              return [
                'class',
                'disabled',
                'aria-disabled',
                'data-option-available',
              ].includes(mutation.attributeName);
            });

            if (relevant) schedule();
          });

          this._pipingPortalObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [
              'class',
              'disabled',
              'aria-disabled',
              'data-option-available',
            ],
          });
        }

        schedule();
      }

      disconnectedCallback() {
        this._pipingFilterTimers.forEach((timer) =>
          window.clearTimeout(timer)
        );
        this._pipingFilterTimers = [];

        this._pipingFilterObserver?.disconnect();
        this._pipingFilterObserver = null;

        this._pipingPortalObserver?.disconnect();
        this._pipingPortalObserver = null;

        if (this._pipingFilterHandlers) {
          const {
            clickHandler,
            changeHandler,
            variantHandler,
          } = this._pipingFilterHandlers;

          document.removeEventListener('click', clickHandler, true);
          document.removeEventListener('change', changeHandler, true);
          document.removeEventListener(
            'variant:change',
            variantHandler,
            true
          );
          document.removeEventListener(
            'variant:changed',
            variantHandler,
            true
          );
          document.removeEventListener(
            'shopify:section:load',
            variantHandler,
            true
          );
        }

        this._pipingFilterHandlers = null;
      }

      ensureVariantIsSelected() {
        this._enableForm();
        setTimeout(() => this._enableForm(), 100);
        setTimeout(() => this._enableForm(), 300);
        setTimeout(() => this._enableForm(), 800);
      }

      _enableForm() {
        let form = this.closest('form');

        if (!form) {
          const sectionId = this.dataset.section;
          if (sectionId) {
            form = document.getElementById(`product-form-${sectionId}`);
          }
        }

        if (!form) return;

        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) variantInput.disabled = false;

        // FIX: If the submit button's data says it IS available and NOT inventory-tracked,
        // remove any inline loading/disabled styles the theme JS may have applied.
        const submitButton = form.querySelector('button[type="submit"][name="add"]');
        if (submitButton) {
          const inventoryTracked = submitButton.dataset.inventoryTracked;
          const variantAvailable = submitButton.dataset.variantAvailable;

          // Only override when inventory is NOT tracked (these products are always purchasable)
          // OR when the variant is explicitly marked available
          if (inventoryTracked === 'false' || variantAvailable === 'true') {
            submitButton.style.removeProperty('pointer-events');
            submitButton.style.removeProperty('opacity');
            submitButton.disabled = false;
          }
        }
      }

      updateFormButtonState() {
        // Availability state is owned by product-info/buy-buttons data attributes.
        // Re-enable the form in case the theme JS applied a loading state after section reload.
        this._enableForm();
      }

      _bindSmoothPriceSync() {
        const priceContainer = this._getPriceContainer();
        if (!priceContainer || this._priceSyncObserver) return;

        this._priceSyncObserver = new MutationObserver(() => {
          if (!this._lastVariant || this._isSyncingPrice) return;

          clearTimeout(this._priceSyncTimer);
          this._priceSyncTimer = setTimeout(() => {
            this._syncVariantPrice(this._lastVariant);
          }, 0);
        });

        this._priceSyncObserver.observe(priceContainer, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      _getSectionId() {
        const productInfo = this.closest('[id^="ProductInfo-"]');
        if (productInfo) return productInfo.id.replace('ProductInfo-', '');
        return this.dataset.section || this.closest('[data-section]')?.dataset.section || '';
      }

      _getPriceContainer() {
        const sectionId = this._getSectionId();
        const productInfo = sectionId ? document.getElementById(`ProductInfo-${sectionId}`) : this.closest('[id^="ProductInfo-"]');
        return (
          (sectionId && document.getElementById(`price-${sectionId}`)) ||
          productInfo?.querySelector('[id^="price-"]') ||
          document.querySelector('[id^="price-"]')
        );
      }

      _formatMoney(cents) {
        if (typeof cents !== 'number') cents = Number(cents || 0);

        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const moneyFormat = window.money_format || window.theme?.moneyFormat || 'AED {{amount_no_decimals}}';
          return Shopify.formatMoney(cents, moneyFormat)
            .replace(/\.00/g, '')
            .replace(/Dhs\./gi, 'AED')
            .replace(/DHS\./g, 'AED');
        }

        return `AED ${Math.round(cents / 100).toLocaleString('en-US')}`;
      }

      _syncVariantPrice(variant) {
        if (!variant || !variant.price) return;

        const priceContainer = this._getPriceContainer();
        if (!priceContainer) return;

        const formattedPrice = this._formatMoney(variant.price);
        const formattedCompareAt = variant.compare_at_price ? this._formatMoney(variant.compare_at_price) : '';

        this._isSyncingPrice = true;

        const priceRoot = priceContainer.querySelector('.f-price');
        if (priceRoot) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceRoot.classList.add('f-price--on-sale');
          } else {
            priceRoot.classList.remove('f-price--on-sale');
          }
        }

        const regularMoney =
          priceContainer.querySelector('.f-price__regular .f-price-item--regular.money') ||
          priceContainer.querySelector('.f-price__regular .f-price-item--regular');

        if (regularMoney && !regularMoney.querySelector('s')) {
          regularMoney.innerHTML = formattedPrice;
        }

        const saleMoney = priceContainer.querySelector('.f-price__sale .f-price-item--sale');
        if (saleMoney) {
          saleMoney.innerHTML = formattedPrice;
        }

        const compareMoney = priceContainer.querySelector('.f-price__sale .f-price-item--regular s, .f-price__regular .f-price-item--regular s');
        if (compareMoney) {
          compareMoney.innerHTML = formattedCompareAt || '';
        }

        if (variant.id) {
          document.querySelectorAll('input.product-variant-id, form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            input.value = variant.id;
            input.dataset.variantId = variant.id;
            input.disabled = false;
          });
        }

        requestAnimationFrame(() => {
          this._isSyncingPrice = false;
        });
      }

      _bindVariantChangedFix() {
        // After every variant:changed event the theme's product-info.js may re-apply
        // pointer-events / opacity loading styles. Strip them if the variant is available.
        document.addEventListener('variant:changed', () => {
          // Give product-info.js time to finish its DOM updates, then restore.
          setTimeout(() => this._enableForm(), 50);
          setTimeout(() => this._enableForm(), 200);
          setTimeout(() => this._enableForm(), 500);
        });
      }
    }
  );
}